import { useEffect, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import JSZip from "jszip";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import PageHeader from "pages/components/PageHeader";
import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

const editorModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ size: ["small", false, "large"] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const editorFormats = ["header", "size", "bold", "italic", "underline", "list", "align", "link"];

function EmailsFaturacaoClientesPreparar() {
  const { showAlert } = useAlert();
  const { userData } = useAuth();

  const params = new URLSearchParams(window.location.search);

  const grupo = params.get("grupo") ?? "";
  const dataIni = params.get("dataini") ?? "";
  const dataFim = params.get("datafim") ?? "";

  const [loading, setLoading] = useState(false);

  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [replyTo, setReplyTo] = useState("");

  const [assunto, setAssunto] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [ficheiros, setFicheiros] = useState([]);
  const [loadingValidacao, setLoadingValidacao] = useState(false);
  const [validacao, setValidacao] = useState(null);

  const [loadingEnvio, setLoadingEnvio] = useState(false);

  useEffect(() => {
    const carregar = async () => {
      if (!grupo) {
        showAlert("Grupo não indicado.", "warning");
        return;
      }

      setLoading(true);

      const [data, err, meta] = await handleApi(
        api.post("EmailsFaturacaoClientes/SelGrupo", {
          grupo,
        })
      );

      setLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      if (meta?.success === false) {
        showAlert(meta?.message ?? "Não foi possível obter a configuração do grupo.", "error");
        return;
      }

      setTo(Array.isArray(data?.to) ? data.to.join("; ") : "");
      setCc(Array.isArray(data?.cc) ? data.cc.join("; ") : "");
      setReplyTo(data?.replyto ?? "");

      setAssunto(
        `[JF Gest] Documentos de faturação - ${formatarData(dataIni)} a ${formatarData(dataFim)}`
      );

      setMensagem(
        `
        <p>Exmos. Srs.,</p>

        <br/>
        <br/>

        <p>
        Seguem em anexo os documentos de faturação referentes ao período de
        <strong>${formatarData(dataIni)} a ${formatarData(dataFim)}</strong>.
        </p>

        <p>
        <strong>Grupo:</strong> ${grupo}
        </p>



        <br/>

        <p>
        Para qualquer questão relacionada com estes documentos pode,
        por favor, responder diretamente a esta mensagem
        (resposta para <strong>${data?.replyto ?? ""}</strong>).
        </p>

        <br/>
        <br/>

        <p>
        Com os melhores cumprimentos,<br />
        <strong>JF Bebidas</strong>
        </p>

        <br/>
        <br/>

        <p class="ql-size-small">
        <em>
            Esta mensagem foi preparada e enviada através da
            <strong>JF Gest</strong>, plataforma de gestão interna da
            <strong>José Francisco Bebidas, S.A.</strong>.
        </em>
        </p>
        `
      );
    };

    carregar();
  }, [grupo, showAlert]);

  const formatarData = (valor) => {
    if (!valor) return "";

    const [ano, mes, dia] = valor.split("-");

    if (!ano || !mes || !dia) return valor;

    return `${dia}/${mes}/${ano}`;
  };

  const validarFicheiros = async (ficheirosParaValidar) => {
    if (!Array.isArray(ficheirosParaValidar) || ficheirosParaValidar.length === 0) {
      setValidacao(null);
      return;
    }

    try {
      setLoadingValidacao(true);

      const [resumo, err, meta] = await handleApi(
        api.post("EmailsFaturacaoClientes/SelResumoDocumentos", {
          grupo,
          dataini: dataIni,
          datafim: dataFim,
        })
      );

      if (err) {
        showAlert(err, "error");
        setValidacao(null);
        return;
      }

      if (meta?.success === false) {
        showAlert(meta?.message ?? "Não foi possível obter os documentos esperados.", "error");
        setValidacao(null);
        return;
      }

      const esperados = Array.isArray(resumo?.documentos)
        ? resumo.documentos.map((x) => String(x?.faturanumero ?? "").trim()).filter(Boolean)
        : [];

      const zips = ficheirosParaValidar.filter((file) => file.name.toLowerCase().endsWith(".zip"));

      const pdfs = ficheirosParaValidar.filter((file) => file.name.toLowerCase().endsWith(".pdf"));

      let nomesPdf = [];

      if (zips.length === 1) {
        const zip = await JSZip.loadAsync(zips[0]);

        nomesPdf = Object.values(zip.files)
          .filter((entry) => !entry.dir && entry.name.toLowerCase().endsWith(".pdf"))
          .map((entry) => {
            const partes = entry.name.replaceAll("\\", "/").split("/");
            return partes[partes.length - 1];
          });
      } else {
        nomesPdf = pdfs.map((file) => file.name);
      }

      const numerosRecebidos = nomesPdf
        .map((nome) =>
          nome.toLowerCase().endsWith(".pdf")
            ? nome.substring(0, nome.length - 4).trim()
            : nome.trim()
        )
        .filter(Boolean);

      const duplicados = numerosRecebidos.filter(
        (numero, index, array) => array.indexOf(numero) !== index
      );

      const recebidosUnicos = [...new Set(numerosRecebidos)];

      const emFalta = esperados.filter((numero) => !recebidosUnicos.includes(numero));

      const inesperados = recebidosUnicos.filter((numero) => !esperados.includes(numero));

      const ok =
        esperados.length > 0 &&
        emFalta.length === 0 &&
        inesperados.length === 0 &&
        duplicados.length === 0 &&
        recebidosUnicos.length === esperados.length;

      setValidacao({
        ok,
        esperados: esperados.length,
        recebidos: recebidosUnicos.length,
        emFalta,
        inesperados,
        duplicados: [...new Set(duplicados)],
      });
    } catch (err) {
      console.error(err);

      showAlert("Não foi possível validar os documentos selecionados.", "error");

      setValidacao(null);
    } finally {
      setLoadingValidacao(false);
    }
  };

  const selecionarFicheiros = async (event) => {
    const selecionados = Array.from(event.target.files ?? []);

    event.target.value = "";

    if (selecionados.length === 0) return;

    const zips = selecionados.filter((file) => file.name.toLowerCase().endsWith(".zip"));

    const pdfs = selecionados.filter((file) => file.name.toLowerCase().endsWith(".pdf"));

    if (zips.length > 0 && pdfs.length > 0) {
      showAlert("Selecione um ficheiro ZIP ou vários PDFs, mas não ambos.", "warning");
      return;
    }

    if (zips.length > 1) {
      showAlert("Só pode selecionar um ficheiro ZIP.", "warning");
      return;
    }

    if (zips.length === 1 && selecionados.length !== 1) {
      showAlert("O ficheiro ZIP deve ser selecionado isoladamente.", "warning");
      return;
    }

    if (zips.length === 0 && pdfs.length !== selecionados.length) {
      showAlert("Apenas são permitidos ficheiros PDF ou ZIP.", "warning");
      return;
    }

    let novosFicheiros;

    if (zips.length === 1) {
      // ZIP substitui qualquer seleção anterior.
      novosFicheiros = selecionados;
    } else {
      // PDFs acumulam-se aos já existentes.
      const existentesPdf = ficheiros.filter((file) => file.name.toLowerCase().endsWith(".pdf"));

      const mapa = new Map();

      [...existentesPdf, ...selecionados].forEach((file) => {
        mapa.set(file.name.toLowerCase(), file);
      });

      novosFicheiros = Array.from(mapa.values());
    }

    setFicheiros(novosFicheiros);

    await validarFicheiros(novosFicheiros);
  };

  const removerFicheiro = async (index) => {
    const restantes = ficheiros.filter((_, i) => i !== index);

    setFicheiros(restantes);

    await validarFicheiros(restantes);
  };

  const enviarEmail = async () => {
    if (!validacao?.ok) {
      showAlert("Os documentos SAP têm de estar completos antes do envio.", "warning");
      return;
    }

    if (!to.trim()) {
      showAlert("Indique pelo menos um destinatário.", "warning");
      return;
    }

    if (!assunto.trim()) {
      showAlert("O assunto é obrigatório.", "warning");
      return;
    }

    if (!mensagem.trim()) {
      showAlert("A mensagem é obrigatória.", "warning");
      return;
    }

    if (ficheiros.length === 0) {
      showAlert("Selecione os documentos SAP.", "warning");
      return;
    }

    const formData = new FormData();

    formData.append("grupo", grupo);
    formData.append("dataini", dataIni);
    formData.append("datafim", dataFim);

    formData.append("to", to);
    formData.append("cc", cc);
    formData.append("replyto", replyTo);

    formData.append("assunto", assunto);
    formData.append("mensagem", mensagem);

    formData.append("idutilizador", userData?.id);

    ficheiros.forEach((file) => {
      formData.append("ficheiros", file);
    });

    try {
      setLoadingEnvio(true);

      const [data, err, meta] = await handleApi(
        api.post("EmailsFaturacaoClientes/EnviarFaturacao", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      if (meta?.success === false) {
        showAlert(meta?.message ?? "Não foi possível enviar a faturação.", "error");
        return;
      }

      showAlert(meta?.message ?? "Faturação enviada com sucesso.", "success");
    } catch (err) {
      console.error(err);

      showAlert("Erro ao enviar a faturação.", "error");
    } finally {
      setLoadingEnvio(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox mt={3}>
        <Card>
          <MDBox p={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDTypography variant="body2">
                  <strong>Grupo:</strong> {grupo || "-"}
                </MDTypography>

                <MDTypography variant="body2">
                  <strong>Período:</strong> {formatarData(dataIni)} a {formatarData(dataFim)}
                </MDTypography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Para"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  helperText="Separar vários endereços por ;"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Cc"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  helperText="Separar vários endereços por ;"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Reply-To"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Assunto"
                  value={assunto}
                  onChange={(e) => setAssunto(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{
                    display: "block",
                    mb: 0.5,
                    ml: 0.5,
                  }}
                >
                  Mensagem
                </MDTypography>

                <MDBox
                  sx={{
                    "& .quill": {
                      backgroundColor: "#fff",
                    },
                    "& .ql-toolbar": {
                      borderTopLeftRadius: "6px",
                      borderTopRightRadius: "6px",
                      borderColor: "#d2d6da",
                    },
                    "& .ql-container": {
                      minHeight: 220,
                      borderBottomLeftRadius: "6px",
                      borderBottomRightRadius: "6px",
                      borderColor: "#d2d6da",
                      fontSize: "0.875rem",
                    },
                    "& .ql-editor": {
                      minHeight: 220,
                      lineHeight: 1.6,
                    },
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={mensagem}
                    onChange={setMensagem}
                    modules={editorModules}
                    formats={editorFormats}
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12}>
                <MDBox
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <MDTypography variant="subtitle2" fontWeight="bold" mb={0.5}>
                    Documentos SAP
                  </MDTypography>

                  <MDTypography variant="caption" color="text">
                    Selecione o ficheiro SAP_Documentos.zip ou vários documentos PDF.
                  </MDTypography>

                  <MDBox mt={2}>
                    <MDButton component="label" color="info" variant="outlined">
                      <Icon sx={{ mr: 1 }}>upload_file</Icon>
                      Selecionar documentos
                      <input
                        hidden
                        type="file"
                        multiple
                        accept=".zip,.pdf,application/zip,application/pdf"
                        onChange={selecionarFicheiros}
                      />
                    </MDButton>
                  </MDBox>

                  {ficheiros.length > 0 && (
                    <MDBox mt={2}>
                      <MDTypography variant="body2" mb={1}>
                        <strong>
                          {ficheiros.length === 1 &&
                          ficheiros[0].name.toLowerCase().endsWith(".zip")
                            ? "ZIP selecionado:"
                            : `Documentos selecionados: ${ficheiros.length}`}
                        </strong>
                      </MDTypography>

                      {ficheiros.map((file, index) => (
                        <MDBox
                          key={`${file.name}-${file.size}-${index}`}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={2}
                          py={0.5}
                        >
                          <MDTypography
                            variant="caption"
                            sx={{
                              overflowWrap: "anywhere",
                              flex: 1,
                            }}
                          >
                            {file.name}
                          </MDTypography>

                          <MDBox display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
                            <MDTypography
                              variant="caption"
                              color="text"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {(file.size / 1024 / 1024).toLocaleString("pt-PT", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              MB
                            </MDTypography>

                            <MDButton
                              color="error"
                              variant="text"
                              size="small"
                              onClick={() => removerFicheiro(index)}
                              sx={{
                                minWidth: "auto",
                                px: 1,
                              }}
                            >
                              <Icon fontSize="small">delete</Icon>
                            </MDButton>
                          </MDBox>
                        </MDBox>
                      ))}

                      {loadingValidacao && (
                        <MDBox mt={2} display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={18} />

                          <MDTypography variant="caption">A validar documentos...</MDTypography>
                        </MDBox>
                      )}

                      {validacao && !loadingValidacao && (
                        <MDBox
                          mt={2}
                          p={1.5}
                          sx={{
                            border: "1px solid",
                            borderColor: validacao.ok ? "success.main" : "warning.main",
                            borderRadius: 1,
                          }}
                        >
                          <MDTypography
                            variant="body2"
                            fontWeight="bold"
                            color={validacao.ok ? "success" : "warning"}
                          >
                            {validacao.ok
                              ? "Todos os documentos SAP estão presentes."
                              : "Os documentos selecionados não correspondem ao período."}
                          </MDTypography>

                          <MDTypography variant="caption" display="block" mt={1}>
                            Esperados: {validacao.esperados}
                          </MDTypography>

                          <MDTypography variant="caption" display="block">
                            Recebidos: {validacao.recebidos}
                          </MDTypography>

                          {validacao.emFalta.length > 0 && (
                            <MDTypography variant="caption" display="block" mt={1}>
                              <strong>Em falta:</strong> {validacao.emFalta.join(", ")}
                            </MDTypography>
                          )}

                          {validacao.inesperados.length > 0 && (
                            <MDTypography variant="caption" display="block" mt={1}>
                              <strong>Não esperados:</strong> {validacao.inesperados.join(", ")}
                            </MDTypography>
                          )}

                          {validacao.duplicados.length > 0 && (
                            <MDTypography variant="caption" display="block" mt={1}>
                              <strong>Duplicados:</strong> {validacao.duplicados.join(", ")}
                            </MDTypography>
                          )}
                        </MDBox>
                      )}

                      <MDBox mt={1.5}>
                        <MDButton
                          color="error"
                          variant="text"
                          onClick={() => {
                            setFicheiros([]);
                            setValidacao(null);
                          }}
                        >
                          <Icon sx={{ mr: 1 }}>delete</Icon>
                          Apagar todos os anexos
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  )}
                </MDBox>
              </Grid>

              <Grid item xs={12}>
                <MDBox
                  display="flex"
                  justifyContent={{ xs: "stretch", sm: "flex-end" }}
                  sx={{
                    "& > button": {
                      width: { xs: "100%", sm: "auto" },
                    },
                  }}
                >
                  <MDButton
                    color="success"
                    variant="gradient"
                    onClick={enviarEmail}
                    disabled={loadingEnvio || loadingValidacao || !validacao?.ok}
                  >
                    {loadingEnvio ? (
                      <>
                        <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />A enviar...
                      </>
                    ) : (
                      "Enviar"
                    )}
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default EmailsFaturacaoClientesPreparar;
